import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { api } from '../../services/api';
import { summarizeNeighborsScanBatch } from '../../utils/neighborsScanUtils';

// NeighborDataContext shares the /tree-mgmt/analysis/neighbors-data-status
// snapshot across every analyser panel so the freshness chip renders
// instantly and every panel sees the same numbers. We always request
// ?golden=true so this matches the tree-optimization backend, which
// reads golden neighbor scans (GetGoldenNeighborsReport), not latest live.
// The provider owns the fetch, refresh, and per-serial scan-trigger helpers;
// panels just consume.

const NeighborDataContext = createContext(null);

export const NeighborDataProvider = ({ children }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scanningSerials, setScanningSerials] = useState([]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getAnalysisDataStatus(true);
      setStatus(data);
      return data;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // mergeFreshness lets panels push the `data_freshness` block from
  // their most recent analyser response into the shared status so the
  // chip updates without refetching /neighbors-data-status after every Run.
  const mergeFreshness = useCallback((freshness) => {
    if (!freshness) return;
    setStatus((prev) => {
      if (!prev) {
        return { freshness, devices: [], stale_threshold_s: freshness.stale_threshold_s };
      }
      return { ...prev, freshness };
    });
  }, []);

  // scanDevices fans out POST /tree-mgmt/{serial}/neighbors for the
  // provided list. Returns a summary; callers show a toast. Scans
  // are heavy (minutes) so we do not poll or block.
  const scanDevices = useCallback(async (serials) => {
    if (!Array.isArray(serials) || serials.length === 0) return { accepted: 0, failed: 0 };
    setScanningSerials((prev) => Array.from(new Set([...prev, ...serials])));
    try {
      const resp = await api.startNeighborsScanBatch(serials);
      return summarizeNeighborsScanBatch(resp, serials.length);
    } catch (_err) {
      return { accepted: 0, failed: serials.length };
    } finally {
      setScanningSerials((prev) => prev.filter((s) => !serials.includes(s)));
    }
  }, []);

  const scanAll = useCallback(async () => {
    try {
      await api.startNeighborsScanAll();
      return { accepted: true };
    } catch (err) {
      return { accepted: false, error: err };
    }
  }, []);

  const value = useMemo(() => ({
    status,
    freshness: status?.freshness || null,
    devices: status?.devices || [],
    loading,
    error,
    refresh,
    mergeFreshness,
    scanDevices,
    scanAll,
    scanningSerials,
  }), [status, loading, error, refresh, mergeFreshness, scanDevices, scanAll, scanningSerials]);

  return (
    <NeighborDataContext.Provider value={value}>
      {children}
    </NeighborDataContext.Provider>
  );
};

export const useNeighborData = () => {
  const ctx = useContext(NeighborDataContext);
  if (!ctx) {
    // Panels may be rendered outside the provider in isolation (e.g.
    // when embedded in another view). Return a safe no-op shape so
    // they degrade gracefully to "no status, no scan helpers".
    return {
      status: null,
      freshness: null,
      devices: [],
      loading: false,
      error: null,
      refresh: async () => null,
      mergeFreshness: () => {},
      scanDevices: async () => ({ accepted: 0, failed: 0 }),
      scanAll: async () => ({ accepted: false }),
      scanningSerials: [],
    };
  }
  return ctx;
};

export default NeighborDataContext;
